import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders, requestProductHelpEmbedding } from '../utils/api';
import { startActivityAwarePolling } from '../utils/activityAwarePolling';

function isAdminUser(user) {
  const type = (user?.accountType || user?.user_type || '').toLowerCase();
  return type === 'admin' || type === 'superadmin';
}

function ProductHelpEmbeddingBanner() {
  const { user } = useAuth();
  const [embeddingStatus, setEmbeddingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/casc/v1/api/help/info?ensure=0`, { headers });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) {
        throw new Error(json?.error || 'Failed to load help status');
      }
      setEmbeddingStatus(json.data?.embeddingStatus || null);
      setError('');
      return json.data?.embeddingStatus || null;
    } catch (err) {
      setError(err.message || 'Failed to load embedding status');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    return () => {
      if (!pollRef.current) return;
      if (typeof pollRef.current === 'function') pollRef.current();
      else clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [loadStatus]);

  useEffect(() => {
    if (pollRef.current) {
      if (typeof pollRef.current === 'function') pollRef.current();
      else clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (embeddingStatus?.pending) {
      pollRef.current = startActivityAwarePolling(loadStatus, 5000, { immediate: false });
    }
    return () => {
      if (pollRef.current) {
        if (typeof pollRef.current === 'function') pollRef.current();
        else clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [embeddingStatus?.pending, loadStatus]);

  const handleRunEmbedding = async () => {
    setActionLoading(true);
    setError('');
    try {
      const result = await requestProductHelpEmbedding();
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to start embedding');
      }
      setEmbeddingStatus(result.data?.embeddingStatus || embeddingStatus);
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Failed to start embedding');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return null;

  const ready = embeddingStatus?.ready;
  const pending = embeddingStatus?.pending;
  const needsEmbedding = embeddingStatus?.needsEmbedding;
  const docCount = embeddingStatus?.docCount ?? 0;

  if (ready || docCount === 0) return null;

  const admin = isAdminUser(user);

  return (
    <div className={`help-embedding-banner${pending ? ' pending' : ''}`} role="status">
      <div className="help-embedding-banner-text">
        <i className={`fas ${pending ? 'fa-spinner fa-spin' : 'fa-triangle-exclamation'}`} aria-hidden="true" />
        {pending ? (
          <span>Building product documentation embeddings… Ask Zagger search will work when this finishes.</span>
        ) : (
          <span>
            Product documentation is not embedded yet. Ask Zagger cannot search help docs until embeddings are built.
            {embeddingStatus?.warning ? ` (${embeddingStatus.warning})` : ''}
          </span>
        )}
      </div>
      {needsEmbedding && admin && (
        <button
          type="button"
          className="help-embedding-banner-btn"
          onClick={handleRunEmbedding}
          disabled={actionLoading || pending}
        >
          {actionLoading ? 'Starting…' : 'Run embedding'}
        </button>
      )}
      {needsEmbedding && !admin && (
        <span className="help-embedding-banner-hint">Ask an admin to run embedding.</span>
      )}
      {error ? <div className="help-embedding-banner-error">{error}</div> : null}
    </div>
  );
}

export default ProductHelpEmbeddingBanner;
